import { createAsyncThunk } from '@reduxjs/toolkit';

import api, { handleApiError } from 'state/api';

export const getOrdersOverviewApi = createAsyncThunk(
  'orders/getOverview',
  async ({ revenueDays = 30 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (revenueDays != null) {
        params.append('revenueDays', String(revenueDays));
      }
      const query = params.toString();
      const response = await api.get(query ? `orders/overview?${query}` : 'orders/overview');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Could not load orders overview' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getOrdersOverviewApi', error, rejectWithValue);
    }
  },
);

export const getOrdersApi = createAsyncThunk(
  'orders/getOrders',
  async (
    {
      page = 1,
      limit = 25,
      search = '',
      searchField = 'all',
      marketplace = '',
      store = '',
      fulfillmentStatus = '',
      paymentStatus = '',
      fulfillmentChannel = '',
      dateFrom = '',
      dateTo = '',
      isPrime = '',
      lineItemCount = '',
      sortBy = 'createdAt-',
    },
    { rejectWithValue },
  ) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        searchField,
        sortBy,
      });
      if (search) params.append('search', search);
      if (marketplace) params.append('marketplace', marketplace);
      if (store) params.append('store', store);
      if (fulfillmentStatus) params.append('fulfillmentStatus', fulfillmentStatus);
      if (paymentStatus) params.append('paymentStatus', paymentStatus);
      if (fulfillmentChannel) params.append('fulfillmentChannel', fulfillmentChannel);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (isPrime) params.append('isPrime', isPrime);
      if (lineItemCount !== '' && lineItemCount != null) {
        const n = parseInt(String(lineItemCount), 10);
        if (Number.isFinite(n) && n >= 1) {
          params.append('lineItemCount', String(n));
        }
      }

      const response = await api.get(`orders?${params.toString()}`);
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Could not load orders' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getOrdersApi', error, rejectWithValue);
    }
  },
);

export const getOrderByIdApi = createAsyncThunk(
  'orders/getOrderById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`orders/${id}`);
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Order not found' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getOrderByIdApi', error, rejectWithValue);
    }
  },
);

export const getOrdersSyncStatusApi = createAsyncThunk(
  'orders/getSyncStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('orders/sync/status');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Could not load sync status' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getOrdersSyncStatusApi', error, rejectWithValue);
    }
  },
);

export const startOrdersSyncApi = createAsyncThunk(
  'orders/startSync',
  async ({ mode = 'backfill', storeIds, backfillFromDate } = {}, { rejectWithValue }) => {
    try {
      const response = await api.post('orders/sync', { mode, storeIds, backfillFromDate });
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({
          message: body.message || body.error || 'Could not start order sync',
        });
      }
      return body.data;
    } catch (error) {
      return handleApiError('startOrdersSyncApi', error, rejectWithValue);
    }
  },
);

export const getOrdersSettingsApi = createAsyncThunk(
  'orders/getSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('orders/settings');
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Could not load order settings' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('getOrdersSettingsApi', error, rejectWithValue);
    }
  },
);

export const updateOrdersSettingsApi = createAsyncThunk(
  'orders/updateSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await api.put('orders/settings', settings);
      const body = response.data;
      if (!body.success) {
        return rejectWithValue({ message: body.message || 'Could not update order settings' });
      }
      return body.data;
    } catch (error) {
      return handleApiError('updateOrdersSettingsApi', error, rejectWithValue);
    }
  },
);
